import { useState, useMemo } from 'react';
import { Search, MapPin, X, ChevronRight, Check } from 'lucide-react';

const CITY_LOCALITIES = {
  'Mumbai': [
    'Bandra West', 'Bandra East', 'Andheri West', 'Andheri East', 'Powai',
    'Worli', 'Lower Parel', 'Juhu', 'Goregaon', 'Malad West',
    'Borivali', 'Thane', 'Navi Mumbai', 'Kharghar', 'Panvel',
  ],
  'Delhi': [
    'Dwarka', 'Vasant Kunj', 'Saket', 'Green Park', 'Hauz Khas',
    'South Extension', 'Greater Kailash', 'Defence Colony', 'Lajpat Nagar',
    'Rohini', 'Pitampura', 'Janakpuri', 'Punjabi Bagh',
  ],
  'Noida': [
    'Sector 150', 'Sector 137', 'Sector 62', 'Sector 44', 'Sector 18',
    'Greater Noida West', 'Noida Extension', 'Sector 75', 'Sector 128',
  ],
  'Gurugram': [
    'Golf Course Road', 'MG Road', 'Sohna Road', 'Sector 56', 'Sector 49',
    'DLF Phase 1-5', 'Sector 82', 'New Gurugram', 'Sector 67', 'Sector 57',
  ],
  'Bangalore': [
    'Whitefield', 'Sarjapur Road', 'HSR Layout', 'Koramangala', 'Indiranagar',
    'Electronic City', 'Marathahalli', 'Hebbal', 'Yelahanka', 'JP Nagar',
    'Bannerghatta Road', 'Devanahalli', 'Thanisandra',
  ],
  'Hyderabad': [
    'Gachibowli', 'Kondapur', 'HITEC City', 'Jubilee Hills', 'Banjara Hills',
    'Madhapur', 'Kukatpally', 'Miyapur', 'Manikonda', 'Financial District',
    'Narsingi', 'Tellapur', 'Kokapet',
  ],
  'Pune': [
    'Hinjewadi', 'Kharadi', 'Wakad', 'Baner', 'Balewadi',
    'Viman Nagar', 'Koregaon Park', 'Hadapsar', 'Undri', 'Kothrud',
  ],
  'Chennai': [
    'OMR (Old Mahabalipuram Road)', 'Anna Nagar', 'Velachery', 'Adyar',
    'T. Nagar', 'Thoraipakkam', 'Sholinganallur', 'Porur', 'Tambaram',
  ],
  'Ahmedabad': [
    'SG Highway', 'Prahlad Nagar', 'Satellite', 'Thaltej', 'Bopal',
    'Ambli', 'Navrangpura', 'Bodakdev', 'South Bopal',
  ],
  'Kolkata': [
    'Salt Lake', 'Rajarhat', 'New Town', 'Ballygunge', 'Alipore',
    'EM Bypass', 'Garia', 'Behala', 'Howrah',
  ],
  'Goa': [
    'Panjim', 'Calangute', 'Candolim', 'Anjuna', 'Vagator',
    'Assagao', 'Porvorim', 'Mapusa', 'Margao',
  ],
};

const ALL_CITIES = Object.keys(CITY_LOCALITIES);

const IndiaLocationSelector = ({ selected = [], onChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCity, setExpandedCity] = useState(null);

  // Derive selected cities and localities
  const selectedMap = useMemo(() => {
    const map = {};
    selected.forEach(loc => {
      const parts = loc.split(' - ');
      const city = parts[0];
      const locality = parts[1] || null;
      if (!map[city]) map[city] = [];
      if (locality) map[city].push(locality);
    });
    return map;
  }, [selected]);

  const toggleLocality = (city, locality) => {
    const key = `${city} - ${locality}`;
    const next = selected.includes(key) ? selected.filter(s => s !== key) : [...selected, key];
    onChange(next);
  };

  const toggleCity = (city) => {
    const cityLocalities = CITY_LOCALITIES[city] || [];
    const cityKeys = cityLocalities.map(l => `${city} - ${l}`);
    const allSelected = cityKeys.every(k => selected.includes(k));
    if (allSelected) {
      onChange(selected.filter(s => !s.startsWith(`${city} - `)));
    } else {
      const others = selected.filter(s => !s.startsWith(`${city} - `));
      onChange([...others, ...cityKeys]);
    }
  };

  const removeSelection = (key) => {
    onChange(selected.filter(s => s !== key));
  };

  const filteredCities = searchQuery
    ? ALL_CITIES.filter(c => {
        if (c.toLowerCase().includes(searchQuery.toLowerCase())) return true;
        const locs = CITY_LOCALITIES[c] || [];
        return locs.some(l => l.toLowerCase().includes(searchQuery.toLowerCase()));
      })
    : ALL_CITIES;

  const getFilteredLocalities = (city) => {
    const locs = CITY_LOCALITIES[city] || [];
    if (!searchQuery) return locs;
    if (city.toLowerCase().includes(searchQuery.toLowerCase())) return locs;
    return locs.filter(l => l.toLowerCase().includes(searchQuery.toLowerCase()));
  };

  return (
    <div className="space-y-4" data-testid="india-location-selector">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25 pointer-events-none" />
        <input
          type="text"
          placeholder="Search cities or localities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/25 focus:border-gold/40 focus:outline-none transition-colors"
          data-testid="location-search-input"
        />
      </div>

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="location-selected-chips">
          {selected.map(s => (
            <span key={s} className="flex items-center gap-1.5 px-3 py-1.5 bg-gold/10 border border-gold/30 rounded-full text-xs font-medium text-gold">
              <MapPin className="w-3 h-3" />
              {s}
              <button onClick={() => removeSelection(s)} className="ml-0.5 hover:text-white transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <span className="text-[10px] text-white/25 self-center ml-1">{selected.length} area{selected.length !== 1 ? 's' : ''} selected</span>
        </div>
      )}

      {/* City list with expandable localities */}
      <div className="max-h-[320px] overflow-y-auto pr-1 space-y-1 scrollbar-thin" data-testid="location-city-list">
        {filteredCities.map(city => {
          const localities = getFilteredLocalities(city);
          const isExpanded = expandedCity === city;
          const cityCount = (selectedMap[city] || []).length;
          const totalLocalities = (CITY_LOCALITIES[city] || []).length;
          const allChecked = cityCount === totalLocalities && totalLocalities > 0;

          return (
            <div key={city} className="rounded-xl overflow-hidden border border-white/[0.04] hover:border-white/10 transition-colors">
              {/* City header */}
              <button
                onClick={() => setExpandedCity(isExpanded ? null : city)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.03] transition-colors"
                data-testid={`city-${city.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-3">
                  <MapPin className={`w-4 h-4 ${cityCount > 0 ? 'text-gold' : 'text-white/25'}`} />
                  <span className="text-sm font-medium text-white">{city}</span>
                  {cityCount > 0 && (
                    <span className="text-[10px] bg-gold/15 text-gold px-2 py-0.5 rounded-full font-semibold">{cityCount}</span>
                  )}
                </div>
                <ChevronRight className={`w-4 h-4 text-white/20 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
              </button>

              {/* Localities */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-white/[0.04]">
                  {/* Select all for city */}
                  <button
                    onClick={() => toggleCity(city)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium mb-2 transition-colors ${
                      allChecked ? 'bg-gold/10 text-gold' : 'bg-white/[0.02] text-white/40 hover:text-white/60'
                    }`}
                    data-testid={`city-select-all-${city.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {allChecked ? <Check className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 border border-white/20 rounded" />}
                    Select all in {city}
                  </button>
                  <div className="flex flex-wrap gap-1.5">
                    {localities.map(loc => {
                      const key = `${city} - ${loc}`;
                      const isSel = selected.includes(key);
                      return (
                        <button
                          key={loc}
                          onClick={() => toggleLocality(city, loc)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                            isSel
                              ? 'border-gold/40 bg-gold/10 text-gold'
                              : 'border-white/[0.06] text-white/40 hover:border-white/15 hover:text-white/60 hover:bg-white/[0.02]'
                          }`}
                          data-testid={`loc-${city.toLowerCase().replace(/\s/g, '-')}-${loc.toLowerCase().replace(/[\s/.()]/g, '-')}`}
                        >
                          {loc}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IndiaLocationSelector;
